import { CalendarIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  formatDateLabel as formatLabel,
  formatLocalDate,
  parseLocalDate,
} from "@/lib/date-utils";
import { cn } from "@/lib/utils";

import type { DateRange } from "react-day-picker";

export function DateRangePicker({
  from,
  to,
  onChange,
  className,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const range: DateRange = {
    from: parseLocalDate(from),
    to: parseLocalDate(to),
  };

  function handleSelect(next: DateRange | undefined) {
    onChange(
      next?.from ? formatLocalDate(next.from) : "",
      next?.to ? formatLocalDate(next.to) : "",
    );
  }

  const label =
    range.from && range.to
      ? `${formatLabel(range.from)} – ${formatLabel(range.to)}`
      : range.from
        ? formatLabel(range.from)
        : "Pilih rentang tanggal";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 justify-start gap-2 text-left font-normal max-w-full",
            !range.from && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="size-4 shrink-0" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 max-w-[calc(100vw-1.5rem)] overflow-hidden"
        align={isMobile ? "center" : "start"}
      >
        <Calendar
          mode="range"
          selected={range}
          onSelect={handleSelect}
          defaultMonth={range.from}
          numberOfMonths={isMobile ? 1 : 2}
        />
        {(range.from || range.to) && (
          <div className="flex justify-end border-t p-2">
            <Button variant="ghost" size="sm" onClick={() => onChange("", "")}>
              Bersihkan
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
